import type { FunctionComponent } from 'react'
import React, { useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { ButtonLink, SignupButton, TypographyV2 as Typography } from '@which/seatbelt'
import {
  PadlockIcon,
  PadlockOpenIcon,
  SolidPadlockIcon,
} from '@which/seatbelt/src/components/Icons/Miscellaneous'

import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react'
import classnames from 'classnames'

import { ArticleTemplateContext } from '../../../pages/article/ArticleTemplateContext'
import { useSubscriptionCostInfo } from '../../hooks/useSubscriptionCostInfo'
import { Link } from '../Link'
import styles from './SinglePadlockCta.module.scss'

export const SinglePadlockCta: FunctionComponent<Props> = ({
  buttonCopy = 'Unlock table',
  displayButtonIcon = true,
  displayStandfirst = true,
  horizontal = false,
  lockedPadlockIcon = true,
  standfirst = 'Get instant access to this and all our scores and recommendations',
  title = 'Sign up to reveal',
  overlineText = '',
  type,
}) => {
  const subscriptionCostInfo = useSubscriptionCostInfo()
  const linkURL = 'https://join.which.co.uk/join/subscribe'
  const smallPrint = subscriptionCostInfo.CTACaptionText

  const { pathname } = useLocation()
  const { articleFreeAccountStatus } = useContext(ArticleTemplateContext)

  /* Health and Wellbeing experiment WHC-024 */
  const healthWellbeingExperiment = useFeatureValue('whc-1403-health-wellbeing-signup', 'OFF')
  const isHealthWellbeingVariant =
    healthWellbeingExperiment === 'variant-1' || healthWellbeingExperiment === 'variant-2'

  let updatedTitle = title
  let updatedButtonCopy = buttonCopy

  if (healthWellbeingExperiment === 'variant-1') {
    updatedTitle = 'Evidence-based health reviews'
    updatedButtonCopy = 'Unlock now'
  } else if (healthWellbeingExperiment === 'variant-2') {
    updatedTitle = 'Live well, backed by real evidence'
    updatedButtonCopy = 'Unlock now'
  }
  /* END Health and Wellbeing experiment WHC-024 */

  /* Free account Unlock experiment */
  const isFreeAccountUnlockEnabled = useFeatureIsOn('acq-free-account-unlock-content')
  const showFreeAccountCta =
    isFreeAccountUnlockEnabled && articleFreeAccountStatus === 'lockContent'

  const freeAccountCtaTitle = 'Sign up to unlock for free'
  const freeAccountCtaStandfirst =
    'Get instant access to these results, plus more reviews of household essentials, by signing up for a free account.'
  const freeAccountLinkURL = `/free-account?freeArticleParam=true&return_url=${encodeURIComponent(
    `${pathname}?hasSignedUpForFreeAccount=true`
  )}`
  /* END Free account Unlock experiment */

  return (
    <div
      className={classnames(styles.ctaWrapper, type === 'desktop' && styles['ctaWrapperDesktop'], {
        [styles.horizontal]: horizontal,
        [styles.greenBackground]: isHealthWellbeingVariant,
      })}
    >
      <div className={styles.icon}>{lockedPadlockIcon ? <PadlockIcon /> : <PadlockOpenIcon />}</div>
      <div className={styles.ctaContent}>
        {overlineText && (
          <Typography textStyle="sb-text-heading-overline" className={styles.overline}>
            {overlineText}
          </Typography>
        )}
        <Typography textStyle="sb-text-heading-small">
          {showFreeAccountCta ? freeAccountCtaTitle : updatedTitle}
        </Typography>
        {displayStandfirst && !isHealthWellbeingVariant && (
          <Typography textStyle="sb-text-body-small-regular" className={styles.contentWrapper}>
            {showFreeAccountCta ? freeAccountCtaStandfirst : standfirst}
          </Typography>
        )}
        {isHealthWellbeingVariant ? (
          <SignupButton
            withPadlock={true}
            buttonDatalayerTrackingId="nstunlockedprovider-signup-cta"
            buttonLink={{
              text: updatedButtonCopy,
              href: showFreeAccountCta ? freeAccountLinkURL : linkURL,
            }}
          />
        ) : (
          <ButtonLink
            appearance="secondary"
            href={showFreeAccountCta ? freeAccountLinkURL : linkURL}
            className={styles.cta}
            data-which-id="nstunlockedprovider-signup-cta"
          >
            {displayButtonIcon && <SolidPadlockIcon width={15} height={15} viewBox="0 0 15 15" />}
            {updatedButtonCopy}
          </ButtonLink>
        )}

        {!showFreeAccountCta && (
          <Typography textStyle="sb-text-body-x-small-regular">{smallPrint}</Typography>
        )}

        <Typography textStyle="sb-text-body-default-regular">
          <span>{showFreeAccountCta ? 'Already have an account?' : 'Already a member?'}</span>
          <br />
          <Link href="/login">Log in</Link>
        </Typography>
      </div>
    </div>
  )
}

type Props = {
  buttonCopy?: string
  displayButtonIcon?: boolean
  displayStandfirst?: boolean
  horizontal?: boolean
  lockedPadlockIcon?: boolean
  standfirst?: string
  title?: string
  overlineText?: string
  type?: string
}
